import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SDTKService } from '../sdtk.service';
import { SDTK } from '../../../model/quote/sdtk/sdtk.model';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-sdtk-vehicles',
  templateUrl: './sdtk-vehicles.component.html',
  styleUrls: ['./sdtk-vehicles.component.css', '../../quote.component.css']
})
export class SdtkVehiclesComponent implements OnInit {
  @Input() staticData: any;
  @Input() modelObject: any;
  @Input() apiModelObject: any;
  @Output() modelObjectUpdated = new EventEmitter<any>();

  frmSaddlery: FormGroup;
  submitted = false;
  helptext = '';

  coverLimitOptions = [];
  storageOptions = [
    { id: 1, label: 'Building' },
    { id: 2, label: 'Shipping Container' }
  ];
  quote_type: number = this.route.snapshot.params.quote_type
  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private modalService: NgbModal,
    private sdtkService: SDTKService,
    private _snackBar: MatSnackBar,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.getCoverLimits();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['apiModelObject'] && this.apiModelObject) {
      this.modelObject = this.mapApiToSDTK(this.apiModelObject);
      if (this.frmSaddlery) {
        this.patchForm();
      }
    } else if (changes['modelObject'] && this.modelObject) {
      if (this.frmSaddlery) {
        this.patchForm();
      }
    }
  }

  initForm() {
    this.frmSaddlery = this.fb.group({
      sdtk_option_id: ['', Validators.required],
      has_high_value_items: ['', Validators.required],
      high_value_items: this.fb.array([]),
      is_legal_owner: ['', Validators.required],
      storage_location: ['', Validators.required],
      agree_security: [false, Validators.requiredTrue],
      agree_owner_payment: [false, Validators.requiredTrue],
      agree_no_riding_establishment: [false, Validators.requiredTrue],
      agree_under_insurance: [false, Validators.requiredTrue]
    });

    // Listen to cover limit changes to update validation
    this.frmSaddlery.get('sdtk_option_id')?.valueChanges.subscribe(() => {
      this.updateItemValidators();
    });
  }

  // Custom validator to check if item value exceeds cover limit
  private maxValueValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      const coverLimit = this.coverLimitSelected;

      if (value && coverLimit && Number(value) > coverLimit) {
        return { 'maxValue': { value: value, max: coverLimit } };
      }
      return null;
    };
  }

  // Update validators for all items when cover limit changes
  private updateItemValidators() {
    this.highValueItems.controls.forEach(control => {
      const valueControl = control.get('value');
      if (valueControl) {
        valueControl.setValidators([Validators.required, this.maxValueValidator()]);
        valueControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  public getCoverLimits() {
    this.sdtkService.getSDTKRatingOptions()
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.coverLimitOptions = response.result;
            // After getting cover limits, patch the form if we have data
            if (this.modelObject) {
              this.patchForm();
            }
          }
        },
        (error) => {
          console.error('Error fetching cover limits:', error);
        }
      );
  }

  get highValueItems(): FormArray {
    return this.frmSaddlery.get('high_value_items') as FormArray;
  }

  createItemGroup(item?: any): FormGroup {
    return this.fb.group({
      make: [item?.make || '', Validators.required],
      model: [item?.model || '', Validators.required],
      type: [item?.type || '', Validators.required],
      colour: [item?.colour || '', Validators.required],
      size: [item?.size || '', Validators.required],
      value: [item?.value || '', [Validators.required, Validators.min(1500), this.maxValueValidator()]]
    });
  }

  addItem() {
    this.highValueItems.push(this.createItemGroup());
  }

  removeItem(index: number) {
    console.log("Index Number");
    console.log(index);
    this.highValueItems.removeAt(index);
  }

  onHighValueChange(event: any) {
    const value = event.value;
    if (value === true && this.highValueItems.length === 0) {
      this.addItem();
    } else if (value === false) {
      this.highValueItems.clear();
    }
  }

  get totalHighValueItems(): number {
    return this.highValueItems.controls.reduce((sum, ctrl) => {
      const val = ctrl.get('value')?.value || 0;
      return sum + Number(val);
    }, 0);
  }

  get coverLimitSelected(): number {
    const selectedId = this.frmSaddlery.get('sdtk_option_id')?.value;
    if (!selectedId || !this.coverLimitOptions.length) return 0;

    const selectedOption = this.coverLimitOptions.find(opt => opt.id === selectedId);
    if (selectedOption && selectedOption.name) {
      const match = selectedOption.name.match(/[\d,]+/);
      if (match) {
        return Number(match[0].replace(/,/g, ''));
      }
    }
    return 0;
  }

  isTotalOverLimit(): boolean {
    return this.totalHighValueItems > this.coverLimitSelected;
  }

  // Check if any individual item exceeds cover limit
  hasItemOverLimit(): boolean {
    const coverLimit = this.coverLimitSelected;
    if (!coverLimit) return false;

    return this.highValueItems.controls.some(ctrl => {
      const value = ctrl.get('value')?.value;
      return value && Number(value) > coverLimit;
    });
  }

  patchForm() {
    if (!this.modelObject) return;

    // Initialize Questions object if it doesn't exist
    if (!this.modelObject.questions) {
      this.modelObject.questions = {};
    }

    // Initialize vehicles array if it doesn't exist
    if (!this.modelObject.vehicles) {
      this.modelObject.vehicles = [];
    }

    const questions = this.modelObject.questions;
    const coverLimit = this.modelObject.sdtk_option_id || '';

    // Map storage location from numeric to string
    let storage_location = '';
    if (questions.storage_location === 1) {
      storage_location = 'BUILDING';
    } else if (questions.storage_location === 2) {
      storage_location = 'SHIPPING CONTAINER';
    }
    const toBool = (v: any) => v === 1 || v === true;

    // Clear existing items in FormArray first
    while (this.highValueItems.length !== 0) {
      this.highValueItems.removeAt(0);
    }

    // Load vehicles - check all vehicles, not just those >= 1500
    console.log('Loading vehicles:', this.modelObject.vehicles);

    if (this.modelObject.vehicles && this.modelObject.vehicles.length > 0) {
      this.modelObject.vehicles.forEach(vehicle => {
        console.log('Adding vehicle:', vehicle);
        this.highValueItems.push(this.createItemGroup(vehicle));
      });
    }
    const hasHighValue =
      questions.has_items_over_1500 === 1 ? true :
        questions.has_items_over_1500 === 0 ? false :
          null;

    const isLegalOwner =
      questions.is_legal_owner === 1 ? true :
        questions.is_legal_owner === 0 ? false :
          null;
    // Patch form values AFTER loading vehicles
    this.frmSaddlery.patchValue({
      sdtk_option_id: coverLimit,
      has_high_value_items: hasHighValue,
      is_legal_owner: isLegalOwner,
      storage_location: questions.storage_location,
      agree_security: toBool(questions.confirm_security_requirements),
      agree_owner_payment: toBool(questions.confirm_non_ownership_claim_terms),
      agree_no_riding_establishment: toBool(questions.confirm_not_used_for_hiring),
      agree_under_insurance: toBool(questions.confirm_under_insurance_clause)
    }, { emitEvent: false });

    this.frmSaddlery.updateValueAndValidity();
  }

  helpTextPopup(content: any, text: string) {
    this.helptext = text;
    this.modalService.open(content, { size: 'lg' });
  }

  onSubmit() {
    this.submitted = true;

    if (this.frmSaddlery.invalid) {
      this.snackBar.open('Please complete all required fields.', '', {
        duration: 5000,
        panelClass: 'text-danger'
      });

      // Mark all fields as touched to show validation errors
      Object.keys(this.frmSaddlery.controls).forEach(key => {
        this.frmSaddlery.get(key)?.markAsTouched();
      });
      this.highValueItems.controls.forEach(group => {
        Object.keys((group as FormGroup).controls).forEach(key => {
          group.get(key)?.markAsTouched();
        });
      });

      // Scroll to first invalid field
      const firstInvalidControl = document.querySelector('.ng-invalid');
      if (firstInvalidControl) {
        (firstInvalidControl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        (firstInvalidControl as HTMLElement).focus();
      }
      return;
    }

    // Check if any individual item exceeds cover limit
    if (this.hasItemOverLimit()) {
      this.snackBar.open('One or more items exceed the selected cover limit. Please adjust item values or increase your cover limit.', '', {
        duration: 7000,
        panelClass: 'text-danger'
      });
      return;
    }

    if (this.isTotalOverLimit()) {
      this.snackBar.open('Total value of specified items exceeds the selected cover limit.', '', {
        duration: 7000,
        panelClass: 'text-danger'
      });
      return;
    }

    const formValue = this.frmSaddlery.value;

    // Initialize data object if it doesn't exist
    if (!this.modelObject.data) {
      this.modelObject.data = {};
    }

    // Initialize questions object if it doesn't exist
    if (!this.modelObject.questions) {
      this.modelObject.questions = {};
    }

    // Update the cover limit
    this.modelObject.sdtk_option_id = formValue.sdtk_option_id;
    this.modelObject.data.sdtk_option_id = formValue.sdtk_option_id;

    // Update Questions object
    Object.assign(this.modelObject.questions, {
      has_items_over_1500: formValue.has_high_value_items ? 1 : 0,
      is_legal_owner: formValue.is_legal_owner ? 1 : 0,
      storage_location: formValue.storage_location,
      confirm_security_requirements: formValue.agree_security ? 1 : 0,
      confirm_non_ownership_claim_terms: formValue.agree_owner_payment ? 1 : 0,
      confirm_not_used_for_hiring: formValue.agree_no_riding_establishment ? 1 : 0,
      confirm_under_insurance_clause: formValue.agree_under_insurance ? 1 : 0
    });

    // Update vehicles array with high value items
    // Keep existing low-value vehicles (value < 1500) if any
    const existingLowValueVehicles = this.modelObject.vehicles?.filter(v =>
      !v.value || Number(v.value) < 1500
    ) || [];

    // Add new high value items from form
    const newHighValueVehicles = formValue.has_high_value_items
      ? formValue.high_value_items.map(item => ({
        make: item.make,
        model: item.model,
        type: item.type,
        colour: item.colour,
        size: item.size,
        value: Number(item.value)
      }))
      : [];

    this.modelObject.vehicles = [...newHighValueVehicles];

    // Store loading flag in data object
    this.modelObject.data.loading_applied = formValue.storage_location === 1;

    const data = {
      "quote_data": {
        "type": this.modelObject.type,
        "sdtk_option_id": this.modelObject.sdtk_option_id,
        "vehicle_details": this.modelObject.vehicles,
        "id": this.modelObject.id,
        "broker_id": this.modelObject.broker_id,
        "company_id": this.modelObject.company_id,
        "sequence_number": this.modelObject.sequenceNumber,
        "policy_start_date": this.modelObject.inception_date,
        "policy_end_date": this.modelObject.cover_to,
        "effective_date": this.quote_type == 3 ? this.modelObject.effectiveDate : null,
        "excess_protect_cover_id": this.modelObject.excess_protect_cover_id,
        "policy_ref_number": this.modelObject.policyRefNumber,
        "quote_meta_id": this.modelObject.quoteMetaId,
        "policy_holder": {
          "title": this.modelObject.proposer.title,
          "first_name": this.modelObject.proposer.first_name,
          "last_name": this.modelObject.proposer.last_name,
          "email": this.modelObject.proposer.email,
          "phone": this.modelObject.proposer.phone,
          "mobile": this.modelObject.proposer.mobile || '',
          "dob": this.modelObject.proposer.dob,
          "company_name": this.modelObject.proposer.company_name || '',
          "trading_name": this.modelObject.proposer.trading_name || '',
          "proposer_type": 1,
          "year": this.modelObject.proposer.year,
          "micro_enterprise": this.modelObject.proposer.micro_enterprise || "",
        },
        "address": {
          "address1": this.modelObject.proposer.address.line1,
          "address2": this.modelObject.proposer.address.line2,
          "address3": this.modelObject.proposer.address.line3,
          "address4": "",
          "address5": "",
          "city": this.modelObject.proposer.address.county,
          "country": "UK",
          "postcode": this.modelObject.proposer.address.postcode,
        },
        "sms": this.modelObject.proposer.sms,
        "post": this.modelObject.proposer.post,
        "is_phone": this.modelObject.proposer.is_phone,
        "is_email": this.modelObject.proposer.is_email,
        "send_email": 0,
        "questions": this.modelObject.questions,
        'mta_reason_id': this.quote_type == 3 || this.quote_type == 4 ? this.modelObject.mta_reason_id : null,
        'mta_reason_other': this.quote_type == 3 || this.quote_type == 4 ? this.modelObject.mta_reason_other : null
      }
    };

    this.sdtkService.saveQuote(data)
      .subscribe(
        (res: any) => {
          if (res.success) {
            if (res.result.quote_data) {
              const apiObj = res.result.quote_data.quote_versions[0];
              this.modelObject = this.mapApiToSDTK(apiObj);
              this.modelObjectUpdated.emit(this.modelObject);
            }
          } else {
            this._snackBar.open(res.message, '', {
              duration: 5000,
              panelClass: 'text-danger'
            });
          }
        },
        (error) => {
          this._snackBar.open('Error saving data. Please try again.', '', {
            duration: 5000,
            panelClass: 'text-danger'
          });
          console.error('Save error:', error);
        }
      );
  }

  private mapApiToSDTK(api: any): SDTK {
    const model = new SDTK();

    model.id = api.id;
    model.sdtk_option_id = api.sdtk_option_id;
    model.type = api.type;
    model.broker_id = api.broker_id;
    model.company_id = api.company_id;
    model.sequenceNumber = api.sequence_number;
    model.policyRefNumber = api.policy_ref_number;
    model.quoteMetaId = api.quote_meta_id;
    model.excess_protect_cover_id = api.excess_protect_cover_id;

    model.inception_date = api.policy_start_date;
    model.cover_to = api.policy_end_date;

    // Add MTA-specific fields
    model.policyId = api.policy_id;
    model.oldPolicyId = api.old_policy_id;
    model.effectiveDate = api.effective_date;

    // proposer
    model.proposer.micro_enterprise = api.policy_holder.is_micro_enterprise;
    model.proposer.first_name = api.policy_holder.first_name;
    model.proposer.last_name = api.policy_holder.last_name;
    model.proposer.title = api.policy_holder.title;
    model.proposer.dob = api.policy_holder.dob;
    model.proposer.email = api.policy_holder.email;
    model.proposer.phone = api.policy_holder.phone;
    model.proposer.is_phone = api.policy_holder.is_phone;
    model.proposer.sms = api.policy_holder.sms;
    model.proposer.post = api.policy_holder.post;
    model.proposer.is_email = api.policy_holder.is_email;
    model.proposer.proposer_type = api.policy_holder.proposer_type;
    model.proposer.company_name = api.policy_holder.company_name;
    model.proposer.trading_name = api.policy_holder.trading_name;
    model.proposer.year = api.policy_holder.year_of_establishment;
    model.proposer.address.line1 = api.policy_holder.address.address1;
    model.proposer.address.line2 = api.policy_holder.address.address2;
    model.proposer.address.line3 = api.policy_holder.address.address3;
    model.proposer.address.line4 = api.policy_holder.address.address4;
    model.proposer.address.county = api.policy_holder.address.city;
    model.proposer.address.postcode = api.policy_holder.address.postcode;
    model.proposer.address.country = api.policy_holder.address.country;
    model.reference = api.quote_ref_number;
    model.breakdown_gross = api.financial_details.breakdown_gross;
    model.ipt_value = api.financial_details.ipt_value;
    model.total_policy_cost = api.financial_details.total_policy_cost;
    // Preserve vehicles data
    model.vehicles = api.vehicles ?? [];
    model.questions = api.sdtk_questions ?? {};

    // financial details
    if (api.financial_details) {
      model.brokerAdminFee = api.financial_details.broker_admin_fee;
      model.brokerComission = api.financial_details.broker_commission;
    }
    model.mta_reason_id = api.mta_reason_id;
    model.mta_reason_other = api.mta_reason_other;
    return model;
  }

  blockDecimal(event: KeyboardEvent) {
    if (event.key === '.' || event.key === ',') {
      event.preventDefault();
    }
  }
  //capitalize first letter
  capitalizeField(event: Event, fieldName: string, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value && value.length > 0) {
      const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);

      const itemGroup = this.highValueItems.at(index) as FormGroup;
      itemGroup.get(fieldName)?.setValue(capitalizedValue, { emitEvent: false });
    }
  }
}